import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.error('Auth check failed:', err.message || err);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(
      `${BACKEND_URL}/api/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(
      `${BACKEND_URL}/api/auth/register`,
      { email, password, name },
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.error('Logout failed:', err.message || err);
    }
    setUser(false);
  };

  const changePassword = async (currentPassword, newPassword) => {
    const response = await axios.post(
      `${BACKEND_URL}/api/auth/change-password`,
      { current_password: currentPassword, new_password: newPassword },
      { withCredentials: true }
    );
    // Clear force_password_change flag locally
    if (user) {
      setUser({ ...user, force_password_change: false });
    }
    return response.data;
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    checkAuth,
    changePassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
